import React, { useState, useRef, useEffect } from "react";
import { X, ChevronDown, Plus, Globe } from "lucide-react";

export const COMMON_LANGUAGES = [
    "English",
    "Hindi",
    "Bengali",
    "Telugu",
    "Marathi",
    "Tamil",
    "Urdu",
    "Gujarati",
    "Kannada",
    "Malayalam",
    "Odia",
    "Punjabi",
    "Assamese",
    "Maithili",
    "Sanskrit",
    "Nepali",
    "Konkani",
    "Sindhi",
    "Dogri",
    "Manipuri",
    "Bodo",
    "Santhali",
    "Kashmiri",
    "Arabic",
    "French",
    "Spanish",
    "German",
    "Russian",
    "Mandarin",
    "Japanese",
    "Portuguese"
];

export default function LanguageMultiSelect({
    value = [],
    onChange,
    placeholder = "Select or type languages spoken...",
    error = null,
    className = ""
}) {
    const [isOpen, setIsOpen] = useState(false);
    const [search, setSearch] = useState("");
    const containerRef = useRef(null);
    const inputRef = useRef(null);

    const selectedList = Array.isArray(value) ? value : [];

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (containerRef.current && !containerRef.current.contains(event.target)) {
                setIsOpen(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    const filteredLanguages = COMMON_LANGUAGES.filter(
        (lang) =>
            lang.toLowerCase().includes(search.trim().toLowerCase()) &&
            !selectedList.some((sel) => sel.toLowerCase() === lang.toLowerCase())
    );

    const isCustomAddAvailable =
        search.trim().length > 0 &&
        !COMMON_LANGUAGES.some(
            (l) => l.toLowerCase() === search.trim().toLowerCase()
        ) &&
        !selectedList.some(
            (sel) => sel.toLowerCase() === search.trim().toLowerCase()
        );

    const handleSelect = (lang) => {
        if (!selectedList.includes(lang)) {
            const updated = [...selectedList, lang];
            onChange(updated);
        }
        setSearch("");
        if (inputRef.current) inputRef.current.focus();
    };

    const handleRemove = (langToRemove) => {
        const updated = selectedList.filter((lang) => lang !== langToRemove);
        onChange(updated);
    };

    const handleKeyDown = (e) => {
        if (e.key === "Enter") {
            e.preventDefault();
            if (filteredLanguages.length > 0) {
                handleSelect(filteredLanguages[0]);
            } else if (isCustomAddAvailable) {
                const formatted =
                    search.trim().charAt(0).toUpperCase() +
                    search.trim().slice(1);
                handleSelect(formatted);
            }
        } else if (e.key === "Backspace" && !search && selectedList.length > 0) {
            handleRemove(selectedList[selectedList.length - 1]);
        }
    };

    return (
        <div className={`relative ${className}`} ref={containerRef}>
            {/* Input Container */}
            <div
                onClick={() => {
                    setIsOpen(true);
                    if (inputRef.current) inputRef.current.focus();
                }}
                className={`min-h-[42px] w-full border rounded-lg px-2.5 py-1.5 bg-white cursor-text flex flex-wrap items-center gap-1.5 transition-colors ${
                    error
                        ? "border-red-300 ring-1 ring-red-200"
                        : isOpen
                        ? "border-primary-500 ring-2 ring-primary-100"
                        : "border-secondary-200 hover:border-secondary-300"
                }`}
            >
                {selectedList.map((lang) => (
                    <span
                        key={lang}
                        className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md text-xs font-semibold bg-primary-50 text-primary-700 border border-primary-200/80 animate-in fade-in zoom-in-95 duration-150"
                    >
                        <Globe size={11} className="text-primary-500" />
                        <span>{lang}</span>
                        <button
                            type="button"
                            onClick={(e) => {
                                e.stopPropagation();
                                handleRemove(lang);
                            }}
                            className="text-primary-400 hover:text-primary-800 hover:bg-primary-100 rounded p-0.5 transition-colors"
                        >
                            <X size={12} />
                        </button>
                    </span>
                ))}

                <input
                    ref={inputRef}
                    type="text"
                    value={search}
                    onChange={(e) => {
                        setSearch(e.target.value);
                        if (!isOpen) setIsOpen(true);
                    }}
                    onFocus={() => setIsOpen(true)}
                    onKeyDown={handleKeyDown}
                    placeholder={selectedList.length === 0 ? placeholder : "Add more..."}
                    className="flex-1 min-w-[120px] text-sm text-secondary-800 bg-transparent border-none outline-none focus:outline-none focus:ring-0 p-1 placeholder-secondary-400"
                />

                <button
                    type="button"
                    onClick={(e) => {
                        e.stopPropagation();
                        setIsOpen((prev) => !prev);
                    }}
                    className="text-secondary-400 hover:text-secondary-600 p-1"
                >
                    <ChevronDown size={16} className={`transition-transform duration-200 ${isOpen ? "rotate-180" : ""}`} />
                </button>
            </div>

            {/* Dropdown Menu */}
            {isOpen && (
                <div className="absolute left-0 right-0 top-full mt-1 bg-white border border-secondary-200 rounded-xl shadow-lg z-50 max-h-60 overflow-y-auto py-1 animate-in fade-in slide-in-from-top-2 duration-150">
                    {/* Quick Popular suggestions if search is empty */}
                    {!search && selectedList.length === 0 && (
                        <div className="px-3 py-1.5 border-b border-secondary-100">
                            <span className="text-[11px] font-semibold text-secondary-400 uppercase tracking-wider">
                                Popular Languages
                            </span>
                            <div className="flex flex-wrap gap-1.5 mt-1.5">
                                {["English", "Hindi", "Bengali", "Telugu", "Marathi", "Tamil", "Gujarati", "Kannada"].map(
                                    (lang) => (
                                        <button
                                            key={lang}
                                            type="button"
                                            onClick={() => handleSelect(lang)}
                                            className="px-2 py-0.5 text-xs rounded bg-secondary-100 hover:bg-primary-100 hover:text-primary-700 text-secondary-700 font-medium transition-colors"
                                        >
                                            + {lang}
                                        </button>
                                    )
                                )}
                            </div>
                        </div>
                    )}

                    {/* Filtered list */}
                    {filteredLanguages.length > 0 ? (
                        filteredLanguages.map((lang) => (
                            <button
                                key={lang}
                                type="button"
                                onClick={() => handleSelect(lang)}
                                className="w-full text-left px-3.5 py-2 text-sm text-secondary-700 hover:bg-primary-50 hover:text-primary-700 flex items-center justify-between transition-colors"
                            >
                                <span className="font-medium">{lang}</span>
                                <Plus size={14} className="text-secondary-400" />
                            </button>
                        ))
                    ) : null}

                    {/* Custom language option */}
                    {isCustomAddAvailable && (
                        <button
                            type="button"
                            onClick={() => {
                                const formatted =
                                    search.trim().charAt(0).toUpperCase() +
                                    search.trim().slice(1);
                                handleSelect(formatted);
                            }}
                            className="w-full text-left px-3.5 py-2 text-sm text-primary-600 hover:bg-primary-50 flex items-center gap-2 border-t border-secondary-100 font-medium transition-colors"
                        >
                            <Plus size={14} />
                            <span>
                                Add "<strong>{search.trim()}</strong>" as language
                            </span>
                        </button>
                    )}

                    {filteredLanguages.length === 0 && !isCustomAddAvailable && (
                        <div className="px-4 py-3 text-sm text-secondary-400 text-center">
                            No matching languages found
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}
